import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { get } from "lodash";
import {
  Box,
  Grid,
  Button,
  Modal,
  TextField,
  Stack,
  Typography
} from "@mui/material";


import "../css/font-awesome-pro.css";
import prediction1Icon from "../images/temp-01.png";
import prediction2Icon from "../images/temp-02.png";
import prediction3Icon from "../images/temp-03.png";
import prediction4Icon from "../images/temp-04.png";

import { getAllDevices, addDevice, updateDevice } from "../api";
import { Fade, InputAdornment, IconButton } from '@mui/material';

function DeviceList() {
  const navigate = useNavigate();

  /* ================= STATE ================= */
  const [devices, setDevices] = useState([]);
  const [isDealer, setIsDealer] = useState(false);

  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const [showDeviceModal, setDeviceModal] = useState(false);
  const [deviceData, setDeviceData] = useState({});
  const [formErr, setFormErr] = useState({});

  /* ================= AUTH + FETCH ================= */
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const dealer = sessionStorage.getItem("isDealer");

    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    setIsDealer(dealer === "true");
    getAllDevices().then(setDevices);
  }, [navigate]);

  /* ================= MACHINE HEALTH ================= */
  const machineHealth = (data) => {
    const runtime = get(data, "data.runtime", 0) / 60;

    if (runtime < 100) return <span><img src={prediction1Icon} width="10" alt="Excellent" /> Excellent</span>;
    if (runtime < 200) return <span><img src={prediction2Icon} width="10" alt="Good" /> Good</span>;
    if (runtime < 300) return <span><img src={prediction3Icon} width="10" alt="Fair" /> Fair</span>;
    return <span><img src={prediction4Icon} width="10" alt="Poor" /> Poor</span>;
  };

  /* ================= FILTER + PAGINATION ================= */
  const filteredDevices = useMemo(() => {
    return devices.filter(d =>
      d.machSerial?.toLowerCase().includes(search.toLowerCase()) ||
      d.deviceId?.toLowerCase().includes(search.toLowerCase())
    );
  }, [devices, search]);

  const totalPages = Math.ceil(filteredDevices.length / pageSize);
  const paginatedDevices = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredDevices.slice(start, start + pageSize);
  }, [filteredDevices, page, pageSize]);

  /* ================= FORM LOGIC ================= */
  const onValueChange = (e) => {
    const { id, value } = e.target;
    setDeviceData(prev => ({ ...prev, [id]: value }));
    setFormErr(prev => ({ ...prev, [id]: value ? "" : "Required" }));
  };

  const isFormError = () => Object.values(formErr).some(Boolean);

  const onSubmit = async () => {
  try {
    const isEdit = Boolean(deviceData._id);
    const action = isEdit ? updateDevice : addDevice;

    await action(deviceData);

    setDeviceModal(false);
    await getAllDevices().then(setDevices);

    alert(isEdit
      ? "Device updated successfully"
      : "Device added successfully"
    );

  } catch (error) {
    console.error(error);
    alert("Something went wrong. Please try again.");
  }
};

  const openAddDevice = () => {
    setDeviceData({
      machSerial: "",
      deviceId: "",
      motorHealth: "",
      flatCutterHealth: "",
      bitCutterHealth: "",
      machinetype: "",
    });
    setFormErr({ machSerial: "Required", deviceId: "Required", machinetype: "Required",});
    setDeviceModal(true);
  };

  const openEditDevice = (device) => {
    setDeviceData(device);
    setFormErr({});
    setDeviceModal(true);
  };
  /* ================= UI ================= */
  return (
    <div className="p-1 md:p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg border shadow">

        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h1 className="text-xl font-semibold">All Devices</h1>
          <Button variant="contained" onClick={openAddDevice}>Add Device</Button>
        </div>

        {/* CONTROLS */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center gap-2 text-sm">
            <span>Show</span>
            <select
              className="border rounded-md px-2 py-1 text-sm"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span>entries</span>
          </div>

          <div className="relative w-full md:w-64">
            <input
              type="search"
              placeholder="Search devices..."
              className="w-full border rounded-md pl-9 pr-3 py-2 text-sm"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <i className="liv-search w-16 absolute left-3 top-2.5 text-gray-400"></i>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-gray-300 border-collapse">
            <thead className="bg-gray-100  border-b-2 border-gray-300">
              <tr>
                <th className="px-4 py-3 text-left border border-gray-300">Serial Number</th>
                <th className="px-4 py-3 text-left border border-gray-300">Device ID</th>
                <th className="px-4 py-3 text-left border border-gray-300">Owner</th>

                {!isDealer && <th className="px-4 py-3 text-left border border-gray-300">Dealer</th>}

                <th className="px-4 py-3 text-left border border-gray-300">Health</th>
                <th className="px-4 py-3 text-left border border-gray-300">Total Key Cut</th>
                <th className="px-4 py-3 text-left border border-gray-300">Status</th>
                <th className="px-4 py-3 text-left border border-gray-300">Action</th>
              </tr>
            </thead>

            <tbody>
              {paginatedDevices.map((d) => (
                <tr key={d._id} className="border-b hover:bg-gray-50">
                  <td
                    className="px-4 py-3 text-blue-600 cursor-pointer border border-gray-300"
                    onClick={() =>
                      navigate(`/home/device/${d.machSerial}`)
                    }
                  >
                    {d.machSerial}
                  </td>

                  <td className="px-4 py-3 border border-gray-300">{d.deviceId}</td>
                  <td className="px-4 py-3 border border-gray-300">{get(d, "owner.fName")}</td>

                  {!isDealer && (
                    <td className="px-4 py-3">
                      {get(d, "dealer.name")}
                    </td>
                  )}

                  <td className="px-4 py-3 border border-gray-300">{machineHealth(d)}</td>
                  <td className="px-4 py-3 border border-gray-300">{get(d, "data.keycount")}</td>

                  <td className="px-4 py-3 border border-gray-300">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        d.isActive ? "bg-green-500" : "bg-red-500"
                      }`}
                    />
                  </td>

                  <td className="px-4 py-3 border border-gray-300">
                        <button
                            type="button"
                            onClick={() => openEditDevice(d)}
                            className="text-blue-600 hover:text-blue-800 bg-transparent border-0 p-0 cursor-pointer"
                            aria-label="Edit device"
                          >
                            <i className="fa-solid fa-pen-to-square"></i>
                          </button>
                  </td>
                </tr>
              ))}

              {paginatedDevices.length === 0 && (
                <tr>
                  <td
                    colSpan={isDealer ? 7 : 8}
                    className="text-center py-6 text-gray-500"
                  >
                    No devices found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="flex items-center justify-between px-6 py-4 border-t text-sm">
          <span>
            Showing {(page - 1) * pageSize + 1}–
            {Math.min(page * pageSize, filteredDevices.length)} of{" "}
            {filteredDevices.length}
          </span>

          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Prev
            </button>
            <button
              disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>

      </div>

    {/* ================= MODAL ================= */}
<Modal
  open={showDeviceModal}
  onClose={() => setDeviceModal(false)}
  closeAfterTransition
  slotProps={{
    backdrop: {
      sx: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)'
      }
    }
  }}
>
  <Fade in={showDeviceModal}>
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: { xs: "90vw", sm: "80vw", md: 700 },
        maxHeight: "90vh",
        bgcolor: "background.paper",
        borderRadius: 3,
        boxShadow: "0 24px 48px rgba(0, 0, 0, 0.2)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column"
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.default"
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "primary.contrastText"
              }}
            >
              <i 
                className={deviceData._id ? "fa-solid fa-pen-to-square" : "fa-solid fa-plus"} 
                style={{ fontSize: 18 }}
              />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {deviceData._id ? "Edit Device" : "Add New Device"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {deviceData._id 
                  ? "Update device information" 
                  : "Enter device details below"}
              </Typography>
            </Box>
          </Stack>

          <IconButton
            onClick={() => setDeviceModal(false)}
            sx={{
              color: "text.secondary",
              "&:hover": { 
                bgcolor: "action.hover",
                color: "text.primary" 
              }
            }}
          >
            <i className="fa-solid fa-xmark" style={{ fontSize: 20 }} />
          </IconButton>
        </Stack>
      </Box>

      {/* Form */}
      <Box
        sx={{
          p: 3,
          overflowY: "auto",
          flex: 1
        }}
      >
        <Grid container spacing={2.5}>
          {/* Device Information Section */}
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <i 
                className="fa-solid fa-circle-info" 
                style={{ color: '#1976d2', fontSize: 16 }}
              />
              <Typography 
                variant="subtitle2" 
                fontWeight={600}
                color="primary.main"
              >
                Device Information
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="machSerial"
              label="Serial Number"
              placeholder="Enter serial number"
              value={deviceData.machSerial || ""}
              onChange={onValueChange}
              error={!!formErr.machSerial}
              helperText={formErr.machSerial}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <i 
                      className="fa-solid fa-hashtag" 
                      style={{ fontSize: 14, color: '#666' }}
                    />
                  </InputAdornment>
                )
              }}

              sx={{
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0
    },
    '& input[type=number]': {
      MozAppearance: 'textfield'
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(48px, 16px) scale(1)', // Increased from 14px to 48px for icon space
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
        backgroundColor: 'background.paper',
        padding: '0 4px'
      }
    }
  }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
           <TextField
                fullWidth
                id="deviceId"
                label="Device ID"
                placeholder="Enter device ID"
                value={deviceData.deviceId || ""}
                onChange={onValueChange}
                error={!!formErr.deviceId}
                helperText={formErr.deviceId}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <i 
                        className="fa-solid fa-barcode" 
                        style={{ fontSize: 14, color: '#666' }}
                      />
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0
                  },
                  '& input[type=number]': {
                    MozAppearance: 'textfield'
                  },
                  '& .MuiInputLabel-outlined': {
                    transform: 'translate(48px, 16px) scale(1)', // Increased from 14px to 48px for icon space
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.75)',
                      backgroundColor: 'background.paper',
                      padding: '0 4px'
                    }
                  }
                }}
              />
          </Grid>

          <Grid item xs={12} md={6}>
           <TextField
                fullWidth
                id="machinetype"
                label="Machine Type"
                placeholder="DUO IQ, Twister II"
                value={deviceData.machinetype || ""}
                onChange={onValueChange}
                error={!!formErr.machinetype}
                helperText={formErr.machinetype}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <i 
                        className="fa-solid fa-barcode" 
                        style={{ fontSize: 14, color: '#666' }}
                      />
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0
                  },
                  '& input[type=number]': {
                    MozAppearance: 'textfield'
                  },
                  '& .MuiInputLabel-outlined': {
                    transform: 'translate(48px, 16px) scale(1)', // Increased from 14px to 48px for icon space
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.75)',
                      backgroundColor: 'background.paper',
                      padding: '0 4px'
                    }
                  }
                }}
              />
          </Grid>

          {/* Health Metrics Section */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <i 
                className="fa-solid fa-heart-pulse" 
                style={{ color: '#1976d2', fontSize: 16 }}
              />
              <Typography 
                variant="subtitle2" 
                fontWeight={600}
                color="primary.main"
              >
                Health Metrics
              </Typography>
            </Stack>
          </Grid>

          {[
            ["motorHealth", "Motor Health", "fa-solid fa-gears"],
            ["flatCutterHealth", "Flat Cutter Health", "fa-solid fa-screwdriver-wrench"],
            ["bitCutterHealth", "Bit Cutter Health", "fa-solid fa-screwdriver"]
          ].map(([id, label, iconClass]) => (
            <Grid item xs={12} md={4} key={id}>
              <TextField
                fullWidth
                id={id}
                label={label}
                placeholder="0-100"
                type="number"
                value={deviceData[id] || ""}
                onChange={onValueChange}
                error={!!formErr[id]}
                helperText={formErr[id]}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <i 
                        className={iconClass} 
                        style={{ fontSize: 14, color: '#666' }}
                      />
                    </InputAdornment>
                  )
                }}
                inputProps={{
                  min: 0,
                  max: 100
                }}

                sx={{
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0
  },
  '& input[type=number]': {
    MozAppearance: 'textfield'
  },
  '& .MuiInputLabel-outlined': {
    transform: 'translate(14px, 16px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
      backgroundColor: 'background.paper',
      padding: '0 4px'
    }
  }
}}

              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Footer Actions */}
      <Box
        sx={{
          p: 3,
          pt: 2,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.default"
        }}
      >
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            size="large"
            variant="outlined"
            onClick={() => setDeviceModal(false)}
            sx={{ 
              minWidth: 120,
              borderColor: "divider",
              color: "text.secondary",
              "&:hover": {
                borderColor: "text.secondary",
                bgcolor: "action.hover"
              }
            }}
            startIcon={<i className="fa-solid fa-xmark" />}
          >
            Cancel
          </Button>
          <Button
            size="large"
            variant="contained"
            onClick={onSubmit}
            disabled={isFormError()}
            startIcon={<i className={deviceData._id ? "fa-solid fa-floppy-disk" : "fa-solid fa-plus"} />}
            sx={{ 
              minWidth: 120,
              boxShadow: 2,
              "&:hover": {
                boxShadow: 4
              }
            }}
          >
            {deviceData._id ? "Save Changes" : "Add Device"}
          </Button>
        </Stack>
      </Box>
    </Box>
  </Fade>
</Modal>

    </div>
  );
}

export default DeviceList;
